import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import Loader from "../../components/Loader";
import ConfirmDialog from "../../components/ConfirmDialog";
import { Search, Plus, Pencil, Trash2, Upload } from "lucide-react";
import { apiClient } from "../../apiclient/apiclient";

const inputClass =
  "w-full h-10 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-800 " +
  "focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400 transition";

const labelClass = "block text-sm font-medium text-slate-600 mb-1";

const Categories = () => {
  const [search, setSearch] = useState("");
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [imagePreview, setImagePreview] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [newCategory, setNewCategory] = useState({
    name: "",
    image: null,
  });

  // For delete confirmation modal
  const [deleteCategoryObj, setDeleteCategoryObj] = useState(null);
  const [deleting, setDeleting] = useState(false);

  /* ================= FETCH ================= */
  useEffect(() => {
    const fetchCategories = async () => {
      setLoading(true);
      try {
        const res = await apiClient.get("/category");
        setCategories(res.data || []);
      } finally {
        setLoading(false);
      }
    };
    fetchCategories();
  }, []);

  /* ================= MODAL ================= */
  const openModal = () => {
    setEditingId(null);
    setNewCategory({ name: "", image: null });
    setImagePreview("");
    setShowModal(true);
  };

  const openEdit = (cat) => {
    setEditingId(cat._id);
    setNewCategory({ name: cat.name, image: null });
    setImagePreview(
      cat.image ? `${process.env.REACT_APP_IMAGE_URL}/${cat.image}` : ""
    );
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setNewCategory({ name: "", image: null });
    setImagePreview("");
  };

  /* ================= IMAGE ================= */
  const handleImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setNewCategory((prev) => ({ ...prev, image: file }));
    setImagePreview(URL.createObjectURL(file));
  };

  /* ================= SAVE ================= */
  const saveCategory = async () => {
  if (!newCategory.name.trim()) return alert("Category name required");

  const formData = new FormData();
  formData.append("name", newCategory.name);

  if (newCategory.image instanceof File) {
    formData.append("uploadImage", newCategory.image);
  }

  try {
    setIsSaving(true); // ⭐ START LOADER

    let res;

    if (editingId) {
      res = await apiClient.put(`/category/${editingId}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setCategories((prev) =>
        prev.map((c) => (c._id === editingId ? res.data.category : c))
      );
    } else {
      res = await apiClient.post("/add-category", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setCategories((prev) => [res.data.category, ...prev]);
    }

    closeModal();
  } catch {
    alert("Error saving category");
  } finally {
    setIsSaving(false); // ⭐ STOP LOADER
  }
};

  /* ================= DELETE ================= */
  const handleDeleteCategory = async () => {
    if (!deleteCategoryObj) return;
    setDeleting(true);
    try {
      await apiClient.delete(`/category/${deleteCategoryObj._id}`);
      setCategories((prev) => prev.filter((c) => c._id !== deleteCategoryObj._id));
      setDeleteCategoryObj(null);
    } catch (err) {
      alert("Failed to delete category");
    } finally {
      setDeleting(false);
    }
  };

  const filteredCategories = categories.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      {/* HEADER - cloned from Brands page */}
      <div className="w-full border-b pt-3 ps-4 pb-4 border-slate-200 bg-blue-50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-6">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Category Management</h1>
          <p className="text-sm text-slate-500 mt-1">Add, edit, or remove product categories.</p>
        </div>
        <button
          onClick={openModal}
          className="px-4 py-2 me-4 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors text-sm font-medium w-auto"
        >
          Add Category
        </button>
      </div>

      {/* SEARCH - cloned from Brands page */}
      <div className="relative mb-6 max-w-md mx-auto">
        <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
          <Search className="w-5 h-5 text-gray-400" />
        </span>
        <input
          placeholder="Search category..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 pr-3 py-2 w-full text-sm bg-white border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition"
        />
      </div>

      {/* GRID (COMPACT LIKE BRANDS) */}
      <div className="min-h-[120px]">
        {loading ? (
          <div className="flex border-t justify-center items-center py-10">
            <Loader size={32} text="Loading Categories...." />
          </div>
        ) : (
          <div className="grid border-t grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {filteredCategories.map((cat) => (
              <div
                key={cat._id}
                className="bg-white p-2 border-b border-e"
              >
                <div>
                  <div className="w-full aspect-square bg-gray-100 rounded-md overflow-hidden border max-h-40">
                    <img
                      src={
                        cat.image
                          ? `${process.env.REACT_APP_IMAGE_URL}/${cat.image}`
                          : "https://via.placeholder.com/300"
                      }
                      alt={cat.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h2 className="text-sm font-medium text-center mt-2 truncate">
                    {cat.name}
                  </h2>
                  <div className="mt-2 flex justify-center gap-2">
                    <button
                      onClick={() => openEdit(cat)}
                      className="p-1.5 rounded-full border-2 border-[#172554] text-white bg-[#172554] hover:bg-white hover:text-[#172554] transition-colors"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setDeleteCategoryObj(cat)}
                      className="p-1.5 rounded-full border-2 border-orange-500 bg-orange-500 text-white hover:bg-white hover:text-orange-500 transition-colors"
                      title="Delete category"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* {filteredCategories.length === 0 && (
        <p className="text-gray-500 mt-6 text-center text-lg">No categories found.</p>
      )} */}

      {/* ================= MODAL (SAME THEME AS BRANDS) ================= */}
      {showModal && createPortal(
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[100] p-2">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              saveCategory();
            }}
            className="bg-white rounded-xl shadow-lg w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden"
          >
            {/* HEADER */}
            <div className="px-6 py-4 bg-[#172554] border-b flex justify-between items-center text-white">
              <h2 className="text-lg font-semibold">
                {editingId ? "Edit Category" : "Add Category"}
              </h2>
              <button type="button" onClick={closeModal} className="text-xl text-white">
                ✕
              </button>
            </div>

            {/* BODY */}
            <div className="p-6 space-y-4 flex-1 overflow-y-auto">
              {/* IMAGE */}
              <div className="flex flex-col items-center">
                <div className="relative w-32 h-32 border rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                  {imagePreview ? (
                    <img
                      src={imagePreview}
                      alt="Category Preview"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-gray-400 text-sm">No Image</span>
                  )}
                </div>

                <label className="mt-3 cursor-pointer bg-orange-500 hover:bg-orange-600 text-white font-semibold px-3 py-2 rounded-md flex items-center gap-2 text-sm">
                  <Upload className="w-4 h-4" /> Upload Image
                  <input type="file" hidden accept="image/*" onChange={handleImage} />
                </label>
              </div>

              {/* NAME */}
              <div>
                <label className="text-xs font-medium text-slate-700">Category Name</label>
                <input
                  type="text"
                  placeholder="Enter category name"
                  value={newCategory.name}
                  onChange={e => setNewCategory(prev => ({ ...prev, name: e.target.value }))}
                  className="border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 py-2 text-sm bg-white outline-none transition w-full"
                  autoComplete="off"
                />
              </div>
            </div>

            {/* FOOTER */}
            <div className="px-6 py-4 border-t bg-white flex justify-end gap-3">
              <button
                type="submit"
                disabled={isSaving}
                className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-6 py-2 rounded-md transition-colors text-sm flex items-center gap-2 disabled:opacity-60"
              >
                {isSaving ? (
                  <Loader text={editingId ? "Updating..." : "Saving..."} inline />
                ) : (
                  editingId ? "Update Category" : "Save Category"
                )}
              </button>
              <button
                type="button"
                onClick={closeModal}
                className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold px-6 py-2 rounded-md transition-colors text-sm border border-slate-200"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>,
        document.body
      )}
    {/* Delete Category Confirmation Modal */}
    <ConfirmDialog
      open={!!deleteCategoryObj}
      title="Delete Category"
      description={`Are you sure you want to delete ${deleteCategoryObj?.name}? This action cannot be undone.`}
      confirmText="Delete"
      cancelText="Cancel"
      variant="danger"
      loading={deleting}
      onCancel={() => setDeleteCategoryObj(null)}
      onConfirm={handleDeleteCategory}
    />
  </div>
  );
};

export default Categories;
